export type PositioningCollisionRect = {
	key: string;
	x: number;
	y: number;
	width: number;
	height: number;
	movable: boolean;
};

type CollisionOptions = {
	anchoredKey?: string;
	iterations?: number;
	padding?: number;
};

export const resolvePositioningCollisions = (
	input: PositioningCollisionRect[],
	{ anchoredKey, iterations = 16, padding = 24 }: CollisionOptions = {}
): PositioningCollisionRect[] => {
	const rects = input.map((rect) => ({ ...rect }));

	for (let iteration = 0; iteration < iterations; iteration += 1) {
		let moved = false;
		// Most nodes on a board are nowhere near each other. Sorting by the
		// horizontal edge lets us stop as soon as the remaining rectangles are
		// too far away, rather than comparing every node with every other node
		// on every animation frame.
		const candidates = [...rects].sort((first, second) => first.x - second.x);
		for (let firstIndex = 0; firstIndex < candidates.length; firstIndex += 1) {
			const first = candidates[firstIndex];
			for (let secondIndex = firstIndex + 1; secondIndex < candidates.length; secondIndex += 1) {
				const second = candidates[secondIndex];
				if (second.x > first.x + first.width + padding) break;
				if (!first.movable && !second.movable) continue;

				const deltaX = second.x + second.width / 2 - (first.x + first.width / 2);
				const deltaY = second.y + second.height / 2 - (first.y + first.height / 2);
				const overlapX = (first.width + second.width) / 2 + padding - Math.abs(deltaX);
				const overlapY = (first.height + second.height) / 2 + padding - Math.abs(deltaY);
				if (overlapX <= 0 || overlapY <= 0) continue;

				let moveFirst = first.movable;
				let moveSecond = second.movable;
				if (first.key === anchoredKey && second.movable) moveFirst = false;
				if (second.key === anchoredKey && first.movable) moveSecond = false;
				if (!moveFirst && !moveSecond) continue;

				const firstShare = moveFirst ? (moveSecond ? 0.5 : 1) : 0;
				const secondShare = moveSecond ? (moveFirst ? 0.5 : 1) : 0;
				if (overlapX < overlapY) {
					const direction =
						deltaX === 0 ? (first.key.localeCompare(second.key) <= 0 ? 1 : -1) : Math.sign(deltaX);
					const separation = overlapX + 0.01;
					first.x -= direction * separation * firstShare;
					second.x += direction * separation * secondShare;
				} else {
					const direction =
						deltaY === 0 ? (first.key.localeCompare(second.key) <= 0 ? 1 : -1) : Math.sign(deltaY);
					const separation = overlapY + 0.01;
					first.y -= direction * separation * firstShare;
					second.y += direction * separation * secondShare;
				}
				moved = true;
			}
		}
		if (!moved) break;
	}

	return rects;
};
